import { type FC, type ReactNode, useState } from 'react'
import classNames from 'classnames'
import { Button } from '../../../../../components/Button/Button'
import {
  type AccountSchedulingSettings,
  CoHostScope,
  EventTypeAccess,
  type EventTypeCoHost,
  type EventTypeCoHosts,
  type EventTypeHost,
  type SchedulingEventType,
} from '../../../../../models/Scheduling'
import { EventTypeImpactDialog } from '../../../../Scheduling/shared/EventTypeImpactDialog'
import { StatusBanner } from '../../../../Scheduling/shared/StatusBanner'
import { isOnAccountPage } from '../../../../Scheduling/shared/eventTypeHostState'
import { SettingsToggle } from '../../sections/SettingsSection'
import { ReactComponent as RotatingHostsIcon } from '../../../../../assets/images/rotating-hosts.svg'
import { type HostPerson, hostDisplayName } from './hostPeople'
import { WarningPill } from './HostRow'
import { ManageHostsModal } from './ManageHostsModal'
import styles from './BookingPages.module.scss'

const ACCOUNT_PAGE_LOCKED_HINT = 'Turn on the account booking page in Setup to use this.'
const HOST_SUMMARY_COUNT = 3
const CO_HOST_SUMMARY_COUNT = 2
const EMPTY_CO_HOSTS_HINT = 'Select co-hosts for this event type. Optional.'

const CO_HOSTS_ROW_HINT: Record<CoHostScope, string> = {
  [CoHostScope.ACCOUNT]: '“Required” co-hosts must attend every meeting booked for this event type.',
  [CoHostScope.INDIVIDUAL]: '“Required” co-hosts must attend every meeting booked on a team member’s page.',
}

type OpenModal = 'hosts' | CoHostScope | null

export type BookingPageToggle = Pick<SchedulingEventType, 'accountPageVisible'> | Pick<SchedulingEventType, 'active'>

const toHost = (person: HostPerson): EventTypeHost => ({ roleIds: [], lastAssignedAt: null, ...person })

const toCoHost = (person: HostPerson): EventTypeCoHost => ({
  userId: person.userId,
  name: person.name,
  email: person.email,
  status: person.status,
  eligible: person.eligible,
  calendar: person.calendar,
  required: true,
})

const summarizeNames = (names: string[], count: number): string => {
  const shown = names.slice(0, count).join(', ')
  const rest = names.length - count
  if (rest <= 0) {
    return shown
  }
  return `${shown} and ${rest} ${rest === 1 ? 'other' : 'others'}`
}

const hostModeBubble = (hosts: EventTypeHost[]): { title: string; hint?: string; isSingle: boolean } | null => {
  const eligibleCount = hosts.filter((host) => host.eligible).length
  if (eligibleCount === 0) {
    return null
  }
  if (hosts.length === 1) {
    return { title: 'One host takes every meeting', isSingle: true }
  }
  return { title: 'Hosts take turns', hint: 'Each booking goes to one of your available hosts.', isSingle: false }
}

const peopleWarnings = (people: HostPerson[]): { key: string; label: string }[] => {
  const withoutCalendar = people.filter((person) => person.status === 'active' && !person.eligible)
  const calendarWarning =
    withoutCalendar.length === 1
      ? [{ key: 'calendar', label: `${hostDisplayName(withoutCalendar[0])} · Calendar not connected` }]
      : [{ key: 'calendar', label: `${withoutCalendar.length} hosts can’t be booked — calendars not connected` }]
  const inactiveWarnings = people
    .filter((person) => person.status !== 'active')
    .map((person) => ({ key: person.userId, label: `${hostDisplayName(person)} · ${person.status ?? 'inactive'}` }))
  return [...(withoutCalendar.length > 0 ? calendarWarning : []), ...inactiveWarnings]
}

interface PeopleWarningsProps {
  people: HostPerson[]
}

const PeopleWarnings: FC<PeopleWarningsProps> = ({ people }) => (
  <>
    {peopleWarnings(people).map((warning) => (
      <WarningPill key={warning.key} className='align-self-start'>
        {warning.label}
      </WarningPill>
    ))}
  </>
)

interface BookingPageCardProps {
  icon: string
  toggle: ReactNode
  children?: ReactNode
}

const BookingPageCard: FC<BookingPageCardProps> = ({ icon, toggle, children }) => (
  <div className={classNames('d-flex flex-column gap-3 p-4 border zocks-border-color-gray-300 zocks-bg-color-white', styles.card)}>
    <div className='d-flex align-items-start zocks-gap-0.75'>
      <i className={classNames('bi d-flex align-items-center fs-5 zocks-text-gray-900', icon, styles.cardIcon)} aria-hidden='true' />
      <div className='flex-grow-1'>{toggle}</div>
    </div>
    {children ? <div className='d-flex flex-column gap-3 zocks-ps-2.25'>{children}</div> : null}
  </div>
)

interface PeopleRowProps {
  label: string
  manageLabel: string
  disabled: boolean
  contentGap: string
  onManage: () => void
  children: ReactNode
}

const PeopleRow: FC<PeopleRowProps> = ({ label, manageLabel, disabled, contentGap, onManage, children }) => (
  <div className={classNames('d-flex flex-wrap align-items-start zocks-gap-1.5 border-top zocks-border-color-gray-200', styles.peopleRow)}>
    <span className={classNames('paragraph-medium fw-semibold flex-shrink-0', styles.labelColumn)}>{label}</span>
    <div className={classNames('d-flex flex-column', contentGap, styles.peopleContent)}>{children}</div>
    <Button
      type='button'
      variant='grayButton'
      className='button-medium zocks-px-0.75'
      aria-label={`${disabled ? 'View' : 'Manage'} ${manageLabel}`}
      onClick={onManage}
    >
      {disabled ? 'View' : 'Manage'}
    </Button>
  </div>
)

interface LoadErrorProps {
  text: string
  onRetry: () => void
}

const LoadError: FC<LoadErrorProps> = ({ text, onRetry }) => (
  <StatusBanner
    tone='warning'
    action={
      <Button type='button' variant='grayButton' className='button-small' onClick={onRetry}>
        Retry
      </Button>
    }
  >
    {text}
  </StatusBanner>
)

interface CoHostsRowProps {
  scope: CoHostScope
  coHosts?: EventTypeCoHost[]
  disabled: boolean
  onManage: () => void
  onRetry: () => void
}

const CoHostsRow: FC<CoHostsRowProps> = ({ scope, coHosts, disabled, onManage, onRetry }) =>
  coHosts ? (
    <PeopleRow label={`Co-hosts (${coHosts.length})`} manageLabel={`${scope} co-hosts`} disabled={disabled} contentGap='zocks-gap-0.25' onManage={onManage}>
      <span className='paragraph-medium fw-semibold'>
        {coHosts.length > 0
          ? summarizeNames(
              coHosts.map((coHost) => `${hostDisplayName(coHost)} (${coHost.required ? 'required' : 'optional'})`),
              CO_HOST_SUMMARY_COUNT,
            )
          : 'No co-hosts added'}
      </span>
      <PeopleWarnings people={coHosts} />
      <span className={classNames('zocks-text-gray-600', styles.secondaryText)}>{coHosts.length > 0 ? CO_HOSTS_ROW_HINT[scope] : EMPTY_CO_HOSTS_HINT}</span>
    </PeopleRow>
  ) : (
    <LoadError text='Could not load the co-hosts.' onRetry={onRetry} />
  )

interface BookingPagesTabProps {
  draft: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  hosts?: EventTypeHost[]
  coHosts?: EventTypeCoHosts
  disabled: boolean
  onToggleChange: (toggle: BookingPageToggle) => Promise<boolean>
  onHostsChange: (hosts: EventTypeHost[]) => Promise<boolean>
  onCoHostsChange: (scope: CoHostScope, coHosts: EventTypeCoHost[]) => Promise<boolean>
  onRetryHosts: () => void
  onRetryCoHosts: () => void
}

export const BookingPagesTab: FC<BookingPagesTabProps> = ({
  draft,
  accountSettings,
  hosts,
  coHosts,
  disabled,
  onToggleChange,
  onHostsChange,
  onCoHostsChange,
  onRetryHosts,
  onRetryCoHosts,
}) => {
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false)
  const [isTogglePending, setIsTogglePending] = useState(false)
  const [openModal, setOpenModal] = useState<OpenModal>(null)
  const accountBookingEnabled = accountSettings?.publicBookingPage.accountBookingEnabled === true
  const isAccountEventType = draft.access === EventTypeAccess.ACCOUNT
  const showsAccountRows = isAccountEventType && accountBookingEnabled && draft.accountPageVisible === true
  const showsIndividualCoHosts = isAccountEventType && draft.active
  const bubble = hosts ? hostModeBubble(hosts) : null

  const saveToggle = async (toggle: BookingPageToggle) => {
    setIsTogglePending(true)
    await onToggleChange(toggle)
    setIsTogglePending(false)
  }

  const handleIndividualPagesChange = (checked: boolean) => {
    if (checked) {
      void saveToggle({ active: true })
    } else {
      setIsDeactivateOpen(true)
    }
  }

  const handleDeactivateConfirm = async () => {
    await saveToggle({ active: false })
    setIsDeactivateOpen(false)
  }

  const saveAndClose = async (save: Promise<boolean>) => {
    if (await save) {
      setOpenModal(null)
    }
  }

  return (
    <div className='d-flex flex-column gap-4'>
      {isAccountEventType ? (
        <BookingPageCard
          icon='bi-buildings'
          toggle={
            <SettingsToggle
              label='Account booking page'
              description='Clients book on your firm’s page. One of your hosts takes each meeting.'
              checked={draft.accountPageVisible === true}
              readOnly={disabled}
              disabled={!accountBookingEnabled || isTogglePending}
              disabledHint={accountBookingEnabled ? undefined : ACCOUNT_PAGE_LOCKED_HINT}
              ariaLabel='Account booking page'
              labelClassName='heading-small'
              descriptionClassName={styles.secondaryText}
              onChange={(checked) => void saveToggle({ accountPageVisible: checked })}
            />
          }
        >
          {showsAccountRows ? (
            <>
              {bubble ? (
                <div className={classNames('d-flex align-items-center zocks-gap-0.75 px-3 py-2 rounded-3 zocks-bg-color-primary-light', styles.callout)}>
                  {bubble.isSingle ? (
                    <i className='bi bi-person fs-5 lh-1 zocks-text-primary flex-shrink-0' aria-label='One host' />
                  ) : (
                    <RotatingHostsIcon className='flex-shrink-0' aria-label='Rotating hosts' />
                  )}
                  <span className='d-flex flex-column'>
                    <span className='paragraph-medium fw-semibold'>{bubble.title}</span>
                    {bubble.hint ? <span className={classNames('zocks-text-gray-600', styles.secondaryText)}>{bubble.hint}</span> : null}
                  </span>
                </div>
              ) : null}
              {hosts ? (
                <PeopleRow
                  label={`Hosts (${hosts.length})`}
                  manageLabel='hosts'
                  disabled={disabled}
                  contentGap='zocks-gap-0.375'
                  onManage={() => setOpenModal('hosts')}
                >
                  <span className='paragraph-medium fw-semibold'>
                    {hosts.length > 0 ? summarizeNames(hosts.map(hostDisplayName), HOST_SUMMARY_COUNT) : 'No hosts added'}
                  </span>
                  {bubble ? null : (
                    <WarningPill className='align-self-start fw-semibold'>Not bookable yet · Clients can’t book until you add a host</WarningPill>
                  )}
                  <PeopleWarnings people={hosts} />
                </PeopleRow>
              ) : (
                <LoadError text='Could not load the hosts.' onRetry={onRetryHosts} />
              )}
              <CoHostsRow
                scope={CoHostScope.ACCOUNT}
                coHosts={coHosts?.[CoHostScope.ACCOUNT]}
                disabled={disabled}
                onManage={() => setOpenModal(CoHostScope.ACCOUNT)}
                onRetry={onRetryCoHosts}
              />
            </>
          ) : null}
        </BookingPageCard>
      ) : null}
      <BookingPageCard
        icon='bi-person'
        toggle={
          <SettingsToggle
            label='Individual booking pages'
            description='Team members can add this event type to their own page. The page owner hosts the meeting.'
            checked={draft.active}
            readOnly={disabled}
            disabled={isTogglePending}
            ariaLabel='Individual booking pages'
            labelClassName='heading-small'
            descriptionClassName={styles.secondaryText}
            onChange={handleIndividualPagesChange}
          />
        }
      >
        {showsIndividualCoHosts ? (
          <CoHostsRow
            scope={CoHostScope.INDIVIDUAL}
            coHosts={coHosts?.[CoHostScope.INDIVIDUAL]}
            disabled={disabled}
            onManage={() => setOpenModal(CoHostScope.INDIVIDUAL)}
            onRetry={onRetryCoHosts}
          />
        ) : null}
      </BookingPageCard>
      {openModal === 'hosts' && hosts ? (
        <ManageHostsModal
          kind='hosts'
          eventTypeId={draft.id}
          people={hosts}
          disabled={disabled}
          toEntry={toHost}
          onDone={(next) => saveAndClose(onHostsChange(next))}
          onCancel={() => setOpenModal(null)}
        />
      ) : null}
      {(openModal === CoHostScope.ACCOUNT || openModal === CoHostScope.INDIVIDUAL) && coHosts ? (
        <ManageHostsModal
          kind='co-hosts'
          eventTypeId={draft.id}
          people={coHosts[openModal]}
          hostIds={openModal === CoHostScope.ACCOUNT ? hosts?.map((host) => host.userId) : undefined}
          disabled={disabled}
          toEntry={toCoHost}
          onDone={(next) => saveAndClose(onCoHostsChange(openModal, next))}
          onCancel={() => setOpenModal(null)}
        />
      ) : null}
      {isDeactivateOpen ? (
        <EventTypeImpactDialog
          variant='deactivate'
          eventType={draft}
          staysOnAccountPage={isAccountEventType && isOnAccountPage(draft, accountBookingEnabled)}
          isLoading={isTogglePending}
          onConfirm={() => void handleDeactivateConfirm()}
          onCancel={() => setIsDeactivateOpen(false)}
        />
      ) : null}
    </div>
  )
}
